// -------------------------------------------------------------------------------------------
// The goal of this file is to allow asynchronous importing of DevExpress components.
// If you import all classes directly in your components, DevExpress will be included in your bundle.
// But DevExpress components are huge (~600KB gzipped), so we want to load them asynchronously.
// -------------------------------------------------------------------------------------------
// HOW TO USE:
// 1. import DevExpress components ONLY from this file (never from 'devexpress-richedit' directly)
// 2. If you need to instantiate some classes use `new DXR.class_name`, e.g. `new DXR.RibbonSelectBoxItem()`;
import { CalculateDocumentVariableAsyncEventArgs } from 'devexpress-richedit/lib/client/document-processor/docvar-args';
import type { DocumentVariableData } from 'devexpress-richedit/lib/client/document-processor/docvar-args';
import type { CharacterPropertiesApi } from 'devexpress-richedit/lib/client/model-api/character-properties';
import type { FieldApi } from 'devexpress-richedit/lib/client/model-api/field';
import type { IntervalApi } from 'devexpress-richedit/lib/client/model-api/interval';
import type { SubDocumentApi } from 'devexpress-richedit/lib/client/model-api/sub-document';
import type { ClientRichEditCore } from 'devexpress-richedit/lib/client/client-rich-edit-core';
import type { SubDocument } from 'devexpress-richedit/lib/common/model/sub-document';
import type { Table } from 'devexpress-richedit/lib/common/model/tables/main-structures/table';
import type { IProcessor } from 'devexpress-richedit/lib/common/processor';
import type { InputPositionBase } from 'devexpress-richedit/lib/common/selection/input-position-base';
import type { SelectionIntervalsInfo } from 'devexpress-richedit/lib/common/selection/selection-intervals-info';

// This is a plain enum and file doesn't import anything.
// So we could import it synchronously (without `import type` and async import in DXR).
// Normally everything from DevExpress should be imported asynchronously!
import { RichEditClientCommand } from 'devexpress-richedit/lib/common/commands/client-command';
import type {
  create,
  createOptions,
  CustomCommandExecutedEventArgs,
  Options,
  RibbonTabType,
  RichEdit,
  SavingEventArgs,
  ViewType,
  RichEditUnit,
  FileTabItemId,
  RibbonButtonItem,
  RibbonSubMenuItem,
  RibbonItemParent,
  PrintMode,
  RibbonSelectBoxItem,
  Ribbon,
  EventArgs,
  Interval,
  SubDocumentBase,
  DocumentFormat,
  DocumentProcessorBase,
  FieldName,
  RibbonMenuItem,
} from 'devexpress-richedit';

export { RichEditClientCommand };
export type {
  Options,
  RichEdit,
  SavingEventArgs,
  CustomCommandExecutedEventArgs,
  RibbonItemParent,
  RibbonTabType,
  PrintMode,
  RibbonSelectBoxItem,
  Ribbon,
  EventArgs,
  SubDocumentBase,
  Interval,
  DocumentFormat,
  DocumentProcessorBase,
  Table,
  IProcessor,
  SubDocument,
  CharacterPropertiesApi,
  FieldApi,
  IntervalApi,
  SubDocumentApi,
  CalculateDocumentVariableAsyncEventArgs,
  DocumentVariableData,
  FieldName,
  RibbonMenuItem,
  ClientRichEditCore,
};
export let DXR: {
  RichEdit: typeof RichEdit;
  create: typeof create;
  createOptions: typeof createOptions;
  CustomCommandExecutedEventArgs: typeof CustomCommandExecutedEventArgs;
  RibbonTabType: typeof RibbonTabType;
  ViewType: typeof ViewType;
  RichEditUnit: typeof RichEditUnit;
  FileTabItemId: typeof FileTabItemId;
  RibbonButtonItem: typeof RibbonButtonItem;
  RibbonSubMenuItem: typeof RibbonSubMenuItem;
  PrintMode: typeof PrintMode;
  RibbonSelectBoxItem: typeof RibbonSelectBoxItem;
  Interval: typeof Interval;
  DocumentFormat: typeof DocumentFormat;
  InputPositionBase: typeof InputPositionBase;
  SelectionIntervalsInfo: typeof SelectionIntervalsInfo;
  FieldName: typeof FieldName;
  RibbonMenuItem: typeof RibbonMenuItem;
  DocumentProcessorBase: typeof DocumentProcessorBase;
} = {} as any;

let _promise: Promise<any> | null;
let _isLoaded: boolean;
let _error: any;

export function useRichEdit() {
  if (!_isLoaded) {
    if (!_promise) {
      _promise = initRichEdit();
    }
    // will trigger Suspense
    throw _promise;
  }
  if (_error) {
    throw _error;
  }
}

function initRichEdit() {
  return Promise.all([
    import('devexpress-richedit').then((x) => {
      DXR = { ...DXR, ...(x as any) };
    }),
    import('devexpress-richedit/lib/common/selection/input-position-base').then(
      (x) => {
        DXR.InputPositionBase = x.InputPositionBase;
      },
    ),
    import(
      'devexpress-richedit/lib/common/selection/selection-intervals-info'
    ).then((x) => {
      DXR.SelectionIntervalsInfo = x.SelectionIntervalsInfo;
    }),
  ])
    .finally(() => (_isLoaded = true))
    .catch((e) => (_error = e));
}
